import type { ReactNode, Ref } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AnimateHeightProps, Height } from 'react-animate-height';
import AnimateHeight from 'react-animate-height';

interface AnimateExpandProps<T extends HTMLElement> {
	isExpanded: boolean;
	children: (contentRef: Ref<T>) => ReactNode;
}

export function AnimateExpand<T extends HTMLElement>({
	isExpanded,
	children,
}: AnimateExpandProps<T>) {
	const [height, setHeight] = useState<Height>(isExpanded ? 'auto' : 0);
	const contentRef = useRef<T>(null);

	const [shouldRender, setShouldRender] = useState(isExpanded);
	useEffect(() => {
		if (isExpanded) {
			setShouldRender(true);
		} else {
			setHeight(0);
		}
	}, [isExpanded]);

	useEffect(() => {
		const element = contentRef.current;
		if (element && isExpanded) {
			const resizeObserver = new ResizeObserver(() => {
				setHeight(element.offsetHeight);
			});

			resizeObserver.observe(element);

			return () => {
				resizeObserver.disconnect();
			};
		}
	}, [shouldRender, isExpanded]);

	const onHeightAnimationEnd = useCallback<
		Required<AnimateHeightProps>['onHeightAnimationEnd']
	>(
		(newHeight) => {
			if (newHeight === 0 && !isExpanded) {
				setShouldRender(false);
			}
		},
		[isExpanded]
	);

	return (
		<AnimateHeight
			duration={500}
			height={height}
			disableDisplayNone
			onHeightAnimationEnd={onHeightAnimationEnd}
		>
			{shouldRender ? children(contentRef) : null}
		</AnimateHeight>
	);
}
